import type { MaterialEntry, MaterialSections, ItemConfig } from '../types';

/**
 * Utilitaires pour manipuler les matériaux
 * Remplace les usages de jsonPath pour une meilleure maintenabilité et performance
 */

/**
 * Trouver un matériau par son nom
 * @param materials Liste des matériaux
 * @param materialName Nom du matériau à trouver
 * @returns Le matériau trouvé ou undefined
 */
export function findMaterialById(materials: MaterialEntry[], materialName: string): MaterialEntry | undefined {
  return materials.find(material => material.material === materialName);
}

/**
 * Filtrer les matériaux par type (extérieur/intérieur)
 * @param materialSections Sections de matériaux (EXT/INT)
 * @param type Type de matériaux à filtrer
 * @returns Liste des matériaux du type spécifié
 */
export function filterMaterialsByType(materialSections: MaterialSections, type: 'exterior' | 'interior'): MaterialEntry[] {
  const section = type === 'exterior' ? materialSections.EXT : materialSections.INT;
  return section || [];
}

/**
 * Pattern de nommage des matériaux windows
 * Les matériaux windows suivent le format: EXT-Window_XXX où XXX est un numéro à 1-3 chiffres
 * Exemples: EXT-Window_1, EXT-Window_01, EXT-Window_001, EXT-Window_100
 */
const WINDOW_MATERIAL_PATTERN = /^EXT-Window_\d{1,3}$/;

/**
 * Vérifie si un matériau est de type window
 * @param materialName Nom du matériau à vérifier
 * @returns true si le matériau est un matériau window
 */
export function isWindowMaterial(materialName: string): boolean {
  return WINDOW_MATERIAL_PATTERN.test(materialName);
}

/**
 * Filtrer les matériaux de type window
 * @param materials Liste des matériaux
 * @returns Liste des matériaux window
 */
export function filterWindowMaterials(materials: MaterialEntry[]): MaterialEntry[] {
  return materials.filter(material => isWindowMaterial(material.material));
}

/**
 * Exclure les matériaux de type window
 * Utile pour ne garder que les matériaux configurables
 * @param materials Liste des matériaux
 * @returns Liste des matériaux sans les windows
 */
export function excludeWindowMaterials(materials: MaterialEntry[]): MaterialEntry[] {
  return materials.filter(material => !isWindowMaterial(material.material));
}

/**
 * Enrichir les matériaux avec les labels depuis la configuration des items
 * @param materials Liste des matériaux à enrichir
 * @param items Configuration des items contenant les labels
 * @returns Liste des matériaux enrichis avec les labels
 */
export function enrichMaterialsWithLabels(materials: MaterialEntry[], items: ItemConfig): MaterialEntry[] {
  return materials.map(material => {
    // Pour chaque matériau, trouver le premier item_id dans sa liste d'items
    // et récupérer son label
    const enrichedMaterial = { ...material };

    if (material.items && material.items.length > 0) {
      // Trouver l'item correspondant dans la configuration
      const item = findItemById(items, material.items[0]);
      if (item && item.label) {
        enrichedMaterial.label = item.label;
      }
    }

    return enrichedMaterial;
  });
}

/**
 * Trouver un item par son ID dans la configuration des items
 * @param items Configuration des items
 * @param itemId ID de l'item à trouver
 * @returns L'item trouvé ou undefined
 */
function findItemById(items: ItemConfig, itemId: string) {
  for (const category of items.ITEMS) {
    for (const group of category.groups) {
      const item = group.items.find(item => item.item_id === itemId);
      if (item) {
        return item;
      }
    }
  }
  return undefined;
}